export interface Activity {
  id: string;
  userId: string;
  userName: string;
  action: string;
  module: string;
  details: string;
  timestamp: string;
  ipAddress?: string;
}

const ACTIVITY_KEY = 'sap_ngecat_activities';
const MAX_ACTIVITIES = 1000; // Keep last 1000 activities

/**
 * Get all activities from localStorage
 */
export const getActivities = (): Activity[] => {
  const stored = localStorage.getItem(ACTIVITY_KEY);
  return stored ? JSON.parse(stored) : [];
};

/**
 * Log a new activity
 */
export const logActivity = (
  userId: string,
  userName: string,
  action: string,
  module: string,
  details: string
): void => {
  const activities = getActivities();
  
  const newActivity: Activity = {
    id: Date.now().toString(),
    userId,
    userName,
    action,
    module,
    details,
    timestamp: new Date().toISOString(),
  };

  activities.push(newActivity);

  // Keep only last MAX_ACTIVITIES
  if (activities.length > MAX_ACTIVITIES) {
    activities.splice(0, activities.length - MAX_ACTIVITIES);
  }

  localStorage.setItem(ACTIVITY_KEY, JSON.stringify(activities));
};

/**
 * Get activities by user
 */
export const getActivitiesByUser = (userId: string): Activity[] => {
  return getActivities().filter(a => a.userId === userId);
};

/**
 * Get activities by module
 */
export const getActivitiesByModule = (module: string): Activity[] => {
  return getActivities().filter(a => a.module === module);
};

/**
 * Get activities by action type
 */
export const getActivitiesByAction = (action: string): Activity[] => {
  return getActivities().filter(a => a.action === action);
};

/**
 * Get activities in date range
 */
export const getActivitiesByDateRange = (startDate: string, endDate: string): Activity[] => {
  return getActivities().filter(a => {
    const actDate = a.timestamp.split('T')[0];
    return actDate >= startDate && actDate <= endDate;
  });
};

/**
 * Get activity statistics
 */
export const getActivityStats = () => {
  const activities = getActivities();
  
  return {
    total: activities.length,
    byAction: activities.reduce((acc, a) => {
      acc[a.action] = (acc[a.action] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    byModule: activities.reduce((acc, a) => {
      acc[a.module] = (acc[a.module] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    byUser: activities.reduce((acc, a) => {
      acc[a.userName] = (acc[a.userName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    lastActivity: activities[activities.length - 1] || null,
  };
};

/**
 * Clear all activities (Admin only)
 */
export const clearActivities = (): void => {
  localStorage.removeItem(ACTIVITY_KEY);
};

/**
 * Export activities as JSON
 */
export const exportActivitiesAsJSON = (): string => {
  const activities = getActivities();
  return JSON.stringify(activities, null, 2);
};

/**
 * Export activities as CSV
 */
export const exportActivitiesAsCSV = (): string => {
  const activities = getActivities();
  
  if (activities.length === 0) return '';

  // CSV Header
  const headers = ['ID', 'User ID', 'User Name', 'Action', 'Module', 'Details', 'Timestamp'];
  const rows = activities.map(a => [
    a.id,
    a.userId,
    a.userName,
    a.action,
    a.module,
    `"${a.details}"`, // Wrap in quotes to handle commas
    a.timestamp,
  ]);

  const csv = [headers, ...rows]
    .map(row => row.join(','))
    .join('\n');

  return csv;
};

/**
 * Log common actions
 */
export const ActivityActions = {
  // Authentication
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  
  // CRUD Operations
  CREATE: 'CREATE',
  READ: 'READ',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  
  // Backup/Restore
  BACKUP_DOWNLOAD: 'BACKUP_DOWNLOAD',
  BACKUP_RESTORE: 'BACKUP_RESTORE',
  
  // Reporting
  PRINT: 'PRINT',
  EXPORT: 'EXPORT',
  
  // User Management
  USER_CREATED: 'USER_CREATED',
  USER_UPDATED: 'USER_UPDATED',
  USER_DELETED: 'USER_DELETED',
  
  // Settings
  SETTINGS_CHANGED: 'SETTINGS_CHANGED',
} as const;

/**
 * Module names
 */
export const Modules = {
  AUTH: 'Authentication',
  DASHBOARD: 'Dashboard',
  PELANGGAN: 'Pelanggan',
  SITE_VISIT: 'Lawatan Tapak',
  SEBUTHARGA: 'Sebutharga',
  INVOICE: 'Invois',
  NILAI_KOS: 'Nilai & Kos Projek',
  KOS_DETAIL: 'Butiran Kos',
  HUTANG: 'Hutang Pelanggan',
  LAPORAN: 'Laporan Kerja',
  GAMBAR: 'Gambar Projek',
  BACKUP: 'Backup & Restore',
  USER_MGMT: 'Pengurusan Pengguna',
} as const;
